
import { ContactAddress } from "@/models/base";
import { DossierConfigService } from "@/services/dossier-config-service";
import { getZipcodes, IZipCode } from "@/utils/zip-code";
import Vue from "vue";
import { getContactServiceByGroupId } from "../../utils/services/contact-services";
import AppCountryCodeAutocomplete from "@/components/CountryCodeAutocomplete.vue";
import AppCustomLanguageSelect from "@/components/CustomLanguageSelect.vue";
import {AuthService, GoogleService} from "@/services";
import {MODULE} from "@/config/modules";
import { ContactsConfig } from "@/models/dossier-configs/contacts";
import AppContactEditAddressEmails from "./ContactEditAddressEmails.vue";
import { ISelectItem } from "winfakt-vue-components";
import { v4 } from "uuid";

let ZipCodes = [] as IZipCode[];

export default Vue.extend({
	data() {
		return {
			country: "",
			multiPostalSelect: false,
			foundPostalCodes: [] as IZipCode[],
			originalCompanyName: "",
			originalAddressline1: "",
			labelInput: "",
			config: DossierConfigService.getContactsConfig() as ContactsConfig,
			autocompleteSessionToken: GoogleService.createAutocompleteAddressSession(),
			googleAutocompleteResults: [] as google.maps.places.PlacePrediction[],
			placesApiAutocompleteThreadId: ""
		};
	},
	props: {
		contactAddress: {
			type: Object as () => ContactAddress,
			required: true
		},
		groupId: {
			type: Number,
			required: true
		},
		hideLabel: Boolean,
		isParticulier: {
			type: Boolean,
			default: false,
			required: false
		},
	},
	computed: {
		lngs: () => DossierConfigService.getLanguages().LanguageNames,
		hasMultiLanguages(): boolean {
			if (!AuthService.hasModule(MODULE.MULTITALEN)) {
				return false;
			}
			return true;
		},
		autocompleteAddressItems():ISelectItem<google.maps.places.PlacePrediction>[] {
			return this.googleAutocompleteResults.map(a=>{
				return {
					text: a.text.text,
					value: a
				};
			});
		}
	},
	methods: {
		async addressBlur(){
			this.googleAutocompleteResults = [];
			if(this.config.CheckForDuplicateAddress && this.contactAddress?.AddressLine1){
				if (this.contactAddress.ContactID != 0) {
					if (this.originalAddressline1 != "" && (this.originalAddressline1 == this.contactAddress.AddressLine1)) {
						return;
					}
				}
				let result = await getContactServiceByGroupId(this.groupId).contactAddressExists(this.contactAddress?.AddressLine1);
				if (result.length == 0) {
					return;
				}
				let foundSelf = false;
				for (let item of result){
					if (item.ID == this.contactAddress.ContactID){
						foundSelf = true;
						break;
					}
				}
				if (!foundSelf) {
					let confirm = await this.$wf.confirm(this.$ct("exists_confirm").toString(),
						this.$t("common.yes").toString(), this.$t("common.no").toString(),
						this.$t("common.warning").toString(), "warning");

					if (!confirm) {
						(this.$refs.AddressLine1 as any).focus();
					} else {
						(this.$refs.AddressLine2 as any).focus();
					}
				}
			}
		},
		postalBlur() {
			let result = ZipCodes.filter(zip => zip.zip == this.contactAddress.Postal);
			if (!result.length) return;

			if (result.length == 1) {
				this.contactAddress.City = result[0].city;
				this.contactAddress.CountryCode = result[0].countryCode;
			} else {
				this.multiPostalSelect = true;
				this.foundPostalCodes = result;
			}
		},
		async fetchZipcodes(code:string){
			ZipCodes = await getZipcodes(["BE", "NL"].includes(code) ? code : undefined);
		},
		cityBlur() {
			console.log(this.contactAddress.Postal);
			if (this.contactAddress.Postal) return;
			let result = ZipCodes.find(zip => zip.city.trim().toLowerCase() == this.contactAddress.City.trim().toLowerCase());
			if (!result) return;
			this.contactAddress.Postal = result.zip;
			this.contactAddress.CountryCode = result.countryCode;
		},
		chooseCity(city: string) {
			this.contactAddress.City = city;
			this.multiPostalSelect = false;
			this.foundPostalCodes = [];
		},
		async companyNameBlur() {
			if (this.contactAddress.ContactID != 0) {
				if (this.originalCompanyName != "" && (this.originalCompanyName == this.contactAddress.CompanyName)) {
					return;
				}
			}
			if (this.contactAddress.CompanyName) {
				let result = await getContactServiceByGroupId(this.groupId).contactExists(this.contactAddress.CompanyName);
				if (result.length == 0) {
					return;
				}
				let foundSelf = false;
				for (let item of result){
					if (item.ID == this.contactAddress.ContactID){
						foundSelf = true;
						break;
					}
				}
				if (!foundSelf) {
					let confirm = await this.$wf.confirm(this.$ct("exists_confirm").toString(),
						this.$t("common.yes").toString(), this.$t("common.no").toString(),
						this.$t("common.warning").toString(), "warning");

					if (!confirm) {
						(this.$refs.CompanyName as any).focus();
					} else {
						(this.$refs.Firstname as any).focus();
					}
				}
			}
		},
		setContactAddressLine1(value:string){
			this.contactAddress.AddressLine1 = value;
			if(this.config.AutoCompleteContactAddress){
				this.doAutocomplete();
			}
		},
		async doAutocomplete(){
			let threadId = v4();
			this.placesApiAutocompleteThreadId = threadId;
			await new Promise(resolve=>setTimeout(resolve, 500));
			if (this.placesApiAutocompleteThreadId != threadId) return;
			this.googleAutocompleteResults = await GoogleService.autocompletAddress(this.contactAddress.AddressLine1, this.autocompleteSessionToken);
		},
		async onAddressCompleteClicked(item:ISelectItem<google.maps.places.PlacePrediction>){
			let address = await GoogleService.autocompletAddressSuggestionToIAddress(item.value);
			this.contactAddress.AddressLine1 = address.address + (address.subpremise ? " " + address.subpremise : "");
			this.contactAddress.City = address.city;
			this.contactAddress.Postal = address.postal;
			this.contactAddress.CountryCode = address.country.toUpperCase();
			console.log(address.country);
		}
	},
	async created(){
		ZipCodes = await getZipcodes(["BE", "NL"].includes(this.contactAddress?.CountryCode) ? this.contactAddress?.CountryCode : undefined);
		if (this.contactAddress.CompanyName) {
			this.originalCompanyName = this.contactAddress.CompanyName;
		}
		if (this.contactAddress?.AddressLine1){
			this.originalAddressline1 = this.contactAddress.AddressLine1;
		}
	},
	components: {
		AppCountryCodeAutocomplete,
		AppCustomLanguageSelect,
		AppContactEditAddressEmails
	},
	i18nEx: {
		componentPrefix: "components.contacts"
	}
});
